import { createSelector } from "reselect";
import { ExtendedProjectInfoDc } from "@evergis/api";

import { isErrorStatus } from "../../resources/layerWithTable";
import { getProjectInfoFromExtendedProjectDc } from "../../evergis/core/project/getProjectInfoFromExtendedProjectDc";
import { getLocationPayload } from "../location/selectors";
import { RootState } from "../reducers";
import { SelectedFilters } from "../../modules/FeatureCard/types";
import { FeatureDataSource } from "../showFeature/types";

export const getProjectState = ({ project }: RootState) => project;

export const getProjectName = createSelector(
  getProjectState,
  getLocationPayload,
  (project, locationPayload) => locationPayload?.project || project?.project?.name || locationPayload?.sharedMap,
);

export const getProjectLayers = createSelector(getProjectState, project => project?.layers || {});

export const getBrokenLayerNames = createSelector(getProjectLayers, (projectLayers): string[] => {
  return Object.entries(projectLayers || {}).reduce<string[]>(
    (layerNames, [name, meta]) => (isErrorStatus(meta.status) ? [...layerNames, name] : layerNames),
    [],
  );
});

export const getProject = createSelector(getProjectState, project =>
  getProjectInfoFromExtendedProjectDc(project?.project),
);

export const getLoading = createSelector(getProjectState, project => !!project?.loading);

export const getError = createSelector(getProjectState, project => project?.error);

export const getProjectTitle = createSelector(getProject, ({ alias, name }) => alias || name);

export const getProjectContent = createSelector(getProject, project => {
  return (project?.content as ExtendedProjectInfoDc)?.content || project?.content || {};
});

export const getProjectItems = createSelector(getProjectContent, content => content?.items || []);

export const getProjectItemsLength = createSelector(getProjectItems, items => items.length);

export const getProjectSrid = createSelector(getProjectContent, content => content?.srid);

export const getProjectBaseMapName = createSelector(getProjectContent, ({ baseMapName }) => baseMapName);

export const getProjectView = createSelector(getProjectContent, ({ position, resolution }) => ({
  position,
  resolution,
}));

export const getClientData = createSelector(getProjectContent, content => content?.clientData || {});

export const getProjectDateFilter = createSelector(getClientData, ({ dateFilter }) => dateFilter);

export const getProjectPage = ({ project }: RootState): number => {
  return project && project.page;
};

export const getProjectDataSources = ({ project }: RootState): FeatureDataSource[] => {
  return project && project.dataSources;
};

export const getProjectDataSourceFilters = ({ project }: RootState): SelectedFilters => {
  return project && project.dataSourceFilters;
};

export const getProjectDataSourcesAreLoading = ({ project }: RootState): boolean => {
  return project && project.dataSourcesAreLoading;
};

export const getSelectedTabId = ({ project }: RootState): string => {
  return project && project.selectedTabId;
};

export const getGeometryFilter = ({ project }: RootState): boolean => {
  return project && project.geometryFilter;
};

export const getSelectedLayers = ({ project }: RootState): string[] => {
  return project && project.selectedLayers;
};
